import { computed, type ComputedRef, type Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { MetadataTemplateFieldTypeEnum } from '@openapi'

export interface TemplateOption {
  label: string
  value: string
}

export interface CanvasStandardFiltersOptions {
  /**
   * Include the "Tags" filter (default: true)
   */
  includeTagsFilter?: boolean
  /**
   * Include the "Draft" filter (default: true)
   */
  includeDraftFilter?: boolean
  /**
   * Include the "Template Used" filter (only for saved canvases)
   */
  includeTemplateFilter?: boolean
  /**
   * Template options for the dropdown filter
   */
  templateOptions?: Ref<TemplateOption[]> | ComputedRef<TemplateOption[]>
  /**
   * Include the "Owner" filter
   */
  includeOwnerFilter?: boolean
  /**
   * Custom label for the Owner filter (defaults to "Owner")
   */
  ownerLabel?: string
}

/**
 * Composable that returns standard filter components for canvas management views
 * @param options Configuration options for which filters to include
 * @returns Array of filter component configurations
 */
export function useCanvasStandardFilters(options: CanvasStandardFiltersOptions = {}) {
  const { t } = useI18n()

  const {
    includeTagsFilter = true,
    includeDraftFilter = true,
    includeTemplateFilter = false,
    templateOptions,
    includeOwnerFilter = false,
    ownerLabel,
  } = options

  const filterComponents = computed(() => {
    const components = []

    // Add Tags filter if enabled
    if (includeTagsFilter) {
      components.push({
        type: MetadataTemplateFieldTypeEnum.TEXT,
        name: 'tags__in',
        label: t('canvasUI.composables.useCanvasStandardFilters.tags'),
      })
    }

    // Add Draft filter if enabled
    if (includeDraftFilter) {
      components.push({
        type: MetadataTemplateFieldTypeEnum.CHECKBOX,
        name: 'is_draft',
        label: t('canvasUI.composables.useCanvasStandardFilters.draft'),
      })
    }

    // Add Owner filter if enabled
    if (includeOwnerFilter) {
      components.push({
        type: MetadataTemplateFieldTypeEnum.TEXT,
        name: 'owned_by__name__icontains',
        label: ownerLabel || t('canvasUI.composables.useCanvasStandardFilters.owner'),
      })
    }

    // Add Template Used filter if option is enabled
    if (includeTemplateFilter) {
      components.push({
        type: MetadataTemplateFieldTypeEnum.SELECT,
        name: 'template__id',
        label: t('canvasUI.composables.useCanvasStandardFilters.templateUsed'),
        options: templateOptions?.value ?? [],
      })
    }

    return components
  })

  return {
    filterComponents,
  }
}
